import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Chip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Card,
  CardMedia,
  FormControlLabel,
  Switch
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { categoryService, videoService } from '../services/api';
import { ModalGridContainer, ModalFormField } from './common/ModalGrid';
import VideoPdfsManager from './VideoPdfsManager';

// Browser-side video thumbnail extraction.
// Falls back gracefully (rejects) on codec issues — caller treats failure as a no-op.
const extractThumbnailFromVideoFile = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const el = document.createElement('video');
  el.preload = 'auto';
  el.muted = true;
  el.playsInline = true;
  el.src = url;

  const cleanup = () => URL.revokeObjectURL(url);

  el.onloadedmetadata = () => {
    const target = Math.min(1, (Number.isFinite(el.duration) ? el.duration : 1) * 0.25);
    el.currentTime = target;
  };

  el.onseeked = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = el.videoWidth || 640;
      canvas.height = el.videoHeight || 360;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        cleanup();
        if (blob) resolve(new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' }));
        else reject(new Error('Canvas toBlob returned null'));
      }, 'image/jpeg', 0.85);
    } catch (err) {
      cleanup();
      reject(err);
    }
  };

  el.onerror = () => {
    cleanup();
    reject(new Error('Failed to load video for thumbnail extraction'));
  };
});

const VideoForm = ({ open, onClose, video = null, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    titleEn: '',
    titleGr: '',
    description: '',
    descriptionEn: '',
    descriptionGr: '',
    categories: [], // Changed to array for multiple categories
    youtubeUrl: '',
    vimeoUrl: '',
    status: 'draft',
    duration: '',
    scheduledAt: '',
    order: 0,
    isPortrait: false,
    pdfs: []
  });
  const [categories, setCategories] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0); // 0 = YouTube, 1 = Upload, 2 = Vimeo
  const [youtubePreview, setYoutubePreview] = useState(null);
  const [youtubeError, setYoutubeError] = useState('');
  const [processingYoutube, setProcessingYoutube] = useState(false);
  const [vimeoPreview, setVimeoPreview] = useState(null);
  const [vimeoError, setVimeoError] = useState('');
  const [processingVimeo, setProcessingVimeo] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  // Separate effect for video data to avoid conflicts
  useEffect(() => {
    if (video && open) {
      // Handle both new categories array and old category field
      let videoCategories = [];
      if (video.categories && video.categories.length > 0) {
        videoCategories = video.categories.map(cat => {
          // Always extract just the ID string
          if (typeof cat === 'object' && cat !== null) {
            return cat._id ? String(cat._id) : '';
          }
          return typeof cat === 'string' ? cat : '';
        }).filter(id => id); // Remove any empty strings
      } else if (video.category) {
        const categoryId = typeof video.category === 'object' && video.category !== null
          ? String(video.category._id || '')
          : String(video.category || '');
        if (categoryId) {
          videoCategories = [categoryId];
        }
      }

      setFormData({
        title: video.title || '',
        titleEn: video.titleEn || '',
        titleGr: video.titleGr || '',
        description: video.description || '',
        descriptionEn: video.descriptionEn || '',
        descriptionGr: video.descriptionGr || '',
        categories: videoCategories,
        youtubeUrl: video.youtubeUrl || '',
        vimeoUrl: video.vimeoUrl || '',
        status: video.status || (video.isPublished ? 'active' : 'draft'),
        duration: video.duration || '',
        scheduledAt: video.scheduledAt ? new Date(video.scheduledAt).toISOString().slice(0, 16) : '',
        order: video.order || 0,
        isPortrait: video.isPortrait === true,
        pdfs: video.pdfs || []
      });

      // Set the appropriate tab based on video type
      if (video.youtubeUrl) {
        setTabValue(0); // YouTube tab
        setYoutubePreview({
          thumbnailUrl: video.thumbnailUrl,
          title: video.title,
          embedUrl: video.embedUrl
        });
      } else if (video.cdnPath) {
        setTabValue(1); // Upload tab
      } else if (video.vimeoUrl) {
        setTabValue(2); // Vimeo tab
        setVimeoPreview({
          thumbnailUrl: video.thumbnailUrl,
          videoId: video.vimeoVideoId,
        });
      }
    }
  }, [video, open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setFormData({
        title: '',
        titleEn: '',
        titleGr: '',
        description: '',
        descriptionEn: '',
        descriptionGr: '',
        categories: [],
        youtubeUrl: '',
        vimeoUrl: '',
        status: 'draft',
        duration: '',
        scheduledAt: '',
        order: 0,
        isPortrait: false,
        pdfs: []
      });
      setVideoFile(null);
      setThumbnailFile(null);
      setYoutubePreview(null);
      setYoutubeError('');
      setVimeoPreview(null);
      setVimeoError('');
      setTabValue(0);
    }
  }, [open]);

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getAll();
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };


  const handleCategoryChange = (event) => {
    const value = event.target.value;
    // Ensure we only store category IDs as strings, not objects
    let categoryIds = [];
    if (typeof value === 'string') {
      categoryIds = value.split(',').filter(id => id);
    } else if (Array.isArray(value)) {
      categoryIds = value.map(item => {
        // Always extract just the string ID
        if (typeof item === 'object' && item !== null) {
          return item._id ? String(item._id) : '';
        }
        return String(item);
      }).filter(id => id); // Remove empty strings
    }

    setFormData({
      ...formData,
      categories: categoryIds
    });
  };

  const handleStatusChange = (event) => {
    const status = event.target.value;
    setFormData({
      ...formData,
      status
    });
  };

  const handleYoutubeUrlChange = async (event) => {
    const url = event.target.value;
    // Auto-flag /shorts/ URLs as portrait. Don't clear isPortrait on a
    // standard URL — the admin may have set it manually.
    const isShortsUrl = /\/shorts\//i.test(url);
    setFormData({
      ...formData,
      youtubeUrl: url,
      isPortrait: isShortsUrl ? true : formData.isPortrait
    });

    setYoutubeError('');
    setYoutubePreview(null);

    if (url.trim()) {
      setProcessingYoutube(true);
      try {
        // Validate YouTube URL format client-side first
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/(watch\?v=|embed\/|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
        if (!youtubeRegex.test(url)) {
          throw new Error('Εισάγετε έγκυρο YouTube URL');
        }

        // Extract video ID for preview
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/);
        if (match && match[1]) {
          const videoId = match[1];

          // Try multiple thumbnail qualities with fallback
          const thumbnailQualities = ['maxresdefault', 'hqdefault', 'mqdefault', 'default'];
          let workingThumbnailUrl = null;

          // Test thumbnail availability
          for (const quality of thumbnailQualities) {
            const testUrl = `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;
            try {
              // Test if thumbnail exists by creating an image element
              await new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(true);
                img.onerror = () => reject();
                img.src = testUrl;
                // Add timeout to avoid hanging
                setTimeout(() => reject(), 3000);
              });
              workingThumbnailUrl = testUrl;
              break;
            } catch {
              continue; // Try next quality
            }
          }

          if (workingThumbnailUrl) {
            setYoutubePreview({
              videoId,
              thumbnailUrl: workingThumbnailUrl,
              embedUrl: `https://www.youtube.com/embed/${videoId}`
            });
          } else {
            // If all thumbnails fail, still show preview with a fallback
            setYoutubePreview({
              videoId,
              thumbnailUrl: `https://img.youtube.com/vi/${videoId}/default.jpg`,
              embedUrl: `https://www.youtube.com/embed/${videoId}`
            });
          }
        }
      } catch (error) {
        setYoutubeError(error.message);
      } finally {
        setProcessingYoutube(false);
      }
    }
  };

  const handleVimeoUrlChange = async (event) => {
    const url = event.target.value;
    setFormData({ ...formData, vimeoUrl: url });
    setVimeoError('');
    setVimeoPreview(null);

    if (!url.trim()) return;
    setProcessingVimeo(true);
    try {
      // Capture an optional privacy hash for unlisted videos (vimeo.com/<id>/<hash>).
      const match = url.match(/(?:player\.vimeo\.com\/video\/|(?:www\.)?vimeo\.com\/(?:video\/)?)(\d{6,12})(?:\/([A-Za-z0-9]+))?/i);
      if (!match || !match[1]) {
        throw new Error('Εισάγετε έγκυρο Vimeo URL');
      }
      const videoId = match[1];
      const hash = match[2] || null;
      // oEmbed works for both public and unlisted videos (the latter only when
      // the hash is included in the source URL). The v2 JSON endpoint returns
      // 404 for unlisted videos, so we don't use it.
      const oembedTarget = hash
        ? `https://vimeo.com/${videoId}/${hash}`
        : `https://vimeo.com/${videoId}`;
      const response = await fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(oembedTarget)}`);
      if (response.status === 403) {
        // Video exists but its privacy/embed settings forbid embedding here.
        throw new Error(t('videos.vimeoForbidden'));
      }
      if (!response.ok) {
        throw new Error('Vimeo βίντεο δεν βρέθηκε');
      }
      const data = await response.json();
      const thumbnailUrl = (data && data.thumbnail_url) || null;
      setVimeoPreview({
        videoId,
        thumbnailUrl,
        embedUrl: hash
          ? `https://player.vimeo.com/video/${videoId}?h=${hash}`
          : `https://player.vimeo.com/video/${videoId}`,
      });
    } catch (error) {
      setVimeoError(error.message);
    } finally {
      setProcessingVimeo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const submitData = new FormData();

      // Handle basic fields
      Object.keys(formData).forEach(key => {
        if (key === 'categories') {
          // Ensure categories only contains string IDs before sending
          const categoryIds = formData.categories.map(cat => {
            // Categories should already be strings at this point, but double-check
            if (typeof cat === 'object' && cat !== null) {
              console.warn('Category object found in form data:', cat);
              return cat._id ? String(cat._id) : null;
            }
            return typeof cat === 'string' ? cat : null;
          }).filter(id => id !== null && id !== '');

          // Send categories as JSON string
          submitData.append('categories', JSON.stringify(categoryIds));
        } else if (key === 'pdfs') {
          // Only send rename/reorder metadata; add/remove flow goes through dedicated routes.
          const pdfMeta = (formData.pdfs || []).map((p, i) => ({
            _id: p._id,
            name: p.name,
            order: typeof p.order === 'number' ? p.order : i,
          }));
          submitData.append('pdfs', JSON.stringify(pdfMeta));
        } else if (formData[key] !== '' && formData[key] !== null && formData[key] !== undefined) {
          submitData.append(key, formData[key]);
        }
      });

      // Handle file uploads (only for traditional upload)
      if (videoFile && tabValue === 1) {
        submitData.append('video', videoFile);
      }
      if (thumbnailFile) {
        submitData.append('thumbnail', thumbnailFile);
      } else if (videoFile && tabValue === 1) {
        try {
          const auto = await extractThumbnailFromVideoFile(videoFile);
          submitData.append('thumbnail', auto);
        } catch (err) {
          console.warn('Auto thumbnail extraction failed; saving without thumbnail.', err);
        }
      }

      // Validate that a URL or video file is provided for new videos
      if (!video) { // New video
        if (tabValue === 0 && !formData.youtubeUrl) {
          throw new Error('YouTube URL is required');
        } else if (tabValue === 1 && !videoFile) {
          throw new Error('Video file is required');
        } else if (tabValue === 2 && !formData.vimeoUrl) {
          throw new Error('Vimeo URL is required');
        }
      }

      // Ensure at least one category is selected
      if (formData.categories.length === 0) {
        throw new Error('Please select at least one category');
      }

      if (video) {
        await videoService.update(video._id, submitData);
      } else {
        await videoService.create(submitData);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving video:', error);
      // You might want to show an error message to the user here
      alert(error.message || 'Error saving video');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {video ? 'Επεξεργασία Βίντεο' : 'Προσθήκη Βίντεο'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
              <Tab label="YouTube Βίντεο" />
              <Tab label="Μεταφόρτωση Αρχείου" />
              <Tab label="Vimeo Βίντεο" />
            </Tabs>
          </Box>

          <ModalGridContainer spacing={3}>
            {/* Video Source Section */}
            {tabValue === 0 && (
              <ModalFormField fullWidth>
                <TextField
                  fullWidth
                  label="YouTube URL"
                  value={formData.youtubeUrl}
                  onChange={handleYoutubeUrlChange}
                  placeholder="https://www.youtube.com/watch?v=..."
                  margin="normal"
                  error={!!youtubeError}
                  helperText={youtubeError || "Εισάγετε το URL του YouTube βίντεο"}
                  slotProps={{
                    input: {
                      endAdornment: processingYoutube && <CircularProgress size={20} />
                    }
                  }}
                />
                {youtubePreview && (
                  <Card sx={{ mt: 2, maxWidth: 400 }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={youtubePreview.thumbnailUrl}
                      alt="YouTube Thumbnail"
                      onError={(e) => {
                        e.target.src = 'https://img.youtube.com/vi/' + youtubePreview.videoId + '/hqdefault.jpg';
                      }}
                    />
                    <Box sx={{ p: 1 }}>
                      <Typography variant="caption" color="success.main">
                        ✓ Έγκυρο YouTube βίντεο
                      </Typography>
                    </Box>
                  </Card>
                )}
              </ModalFormField>
            )}
            {tabValue === 1 && (
              <>
                {video?.videoUrl && !video?.youtubeUrl && !video?.vimeoUrl && (
                  <ModalFormField fullWidth>
                    <video
                      key={video.videoUrl}
                      src={video.videoUrl}
                      poster={video.thumbnailUrl || undefined}
                      controls
                      style={{ width: '100%', maxHeight: 360, backgroundColor: '#000', borderRadius: 4 }}
                    />
                  </ModalFormField>
                )}
                <ModalFormField>
                  <Box>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => setVideoFile(e.target.files[0])}
                      style={{ margin: '16px 0' }}
                    />
                    <Box component="span" sx={{ ml: 1 }}>Αρχείο Βίντεο</Box>
                  </Box>
                </ModalFormField>
              </>
            )}
            {tabValue === 2 && (
              <ModalFormField fullWidth>
                <TextField
                  fullWidth
                  label="Vimeo URL"
                  value={formData.vimeoUrl}
                  onChange={handleVimeoUrlChange}
                  placeholder="https://vimeo.com/123456789"
                  margin="normal"
                  error={!!vimeoError}
                  helperText={vimeoError || "Εισάγετε το URL του Vimeo βίντεο"}
                  slotProps={{
                    input: {
                      endAdornment: processingVimeo && <CircularProgress size={20} />
                    }
                  }}
                />
                {vimeoPreview && vimeoPreview.thumbnailUrl && (
                  <Card sx={{ mt: 2, maxWidth: 400 }}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={vimeoPreview.thumbnailUrl}
                      alt="Vimeo Thumbnail"
                    />
                    <Box sx={{ p: 1 }}>
                      <Typography variant="caption" color="success.main">
                        ✓ Έγκυρο Vimeo βίντεο
                      </Typography>
                    </Box>
                  </Card>
                )}
              </ModalFormField>
            )}

            {/* Custom Thumbnail Upload (for both types) */}
            <ModalFormField>
              <Box>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setThumbnailFile(e.target.files[0])}
                  style={{ margin: '16px 0' }}
                />
                <Box component="span" sx={{ ml: 1 }}>
                  Προσαρμοσμένο Thumbnail (Προαιρετικό)
                </Box>
              </Box>
            </ModalFormField>

            {/* Title Fields - Side by Side */}
            <ModalFormField mobile={12} desktop={12}>
              <TextField
                fullWidth
                label="Τίτλος (Ελληνικά)"
                value={formData.titleGr}
                onChange={handleChange('titleGr')}
                required
                margin="normal"
              />
            </ModalFormField>
            <ModalFormField mobile={12} desktop={12}>
              <TextField
                fullWidth
                label="Title (English)"
                value={formData.titleEn}
                onChange={handleChange('titleEn')}
                margin="normal"
              />
            </ModalFormField>

            {/* Description Fields - Side by Side */}
            <ModalFormField mobile={12} desktop={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Περιγραφή (Ελληνικά)"
                value={formData.descriptionGr}
                onChange={handleChange('descriptionGr')}
                margin="normal"
              />
            </ModalFormField>
            <ModalFormField mobile={12} desktop={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Description (English)"
                value={formData.descriptionEn}
                onChange={handleChange('descriptionEn')}
                margin="normal"
              />
            </ModalFormField>

            {/* Multiple Categories Selection */}
            <ModalFormField fullWidth>
              <FormControl fullWidth margin="normal">
                <InputLabel>Κατηγορίες</InputLabel>
                <Select
                  multiple
                  value={formData.categories}
                  onChange={handleCategoryChange}
                  input={<OutlinedInput label="Κατηγορίες" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const category = categories.find(cat => String(cat._id) === String(value));
                        return (
                          <Chip
                            key={value}
                            label={category ? `${category.nameGr} / ${category.nameEn}` : value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {categories.map((category) => (
                    <MenuItem key={category._id} value={String(category._id)}>
                      <Checkbox checked={formData.categories.indexOf(String(category._id)) > -1} />
                      <ListItemText primary={`${category.nameGr} / ${category.nameEn}`} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </ModalFormField>

            {/* Status Selection */}
            <ModalFormField>
              <FormControl fullWidth margin="normal">
                <InputLabel>Κατάσταση</InputLabel>
                <Select
                  value={formData.status}
                  onChange={handleStatusChange}
                  label="Κατάσταση"
                >
                  <MenuItem value="draft">Πρόχειρο</MenuItem>
                  <MenuItem value="unlisted">Μη Εμφανές</MenuItem>
                  <MenuItem value="active">Ενεργό</MenuItem>
                </Select>
              </FormControl>
            </ModalFormField>

            {/* Duration */}
            <ModalFormField>
              <TextField
                fullWidth
                type="number"
                label="Διάρκεια (δευτερόλεπτα)"
                value={formData.duration}
                onChange={handleChange('duration')}
                margin="normal"
              />
            </ModalFormField>

            {/* Scheduled Publishing */}
            <ModalFormField>
              <TextField
                fullWidth
                type="datetime-local"
                label="Προγραμματισμένη Δημοσίευση"
                value={formData.scheduledAt}
                onChange={handleChange('scheduledAt')}
                margin="normal"
                slotProps={{
                  inputLabel: { shrink: true }
                }}
                helperText="Αφήστε κενό για άμεση δημοσίευση"
              />
            </ModalFormField>

            {/* Order */}
            <ModalFormField>
              <TextField
                fullWidth
                type="number"
                label="Σειρά"
                value={formData.order}
                onChange={handleChange('order')}
                margin="normal"
                helperText="Χρησιμοποιείται για την ταξινόμηση των βίντεο"
              />
            </ModalFormField>

            {/* Portrait orientation toggle */}
            <ModalFormField>
              <FormControlLabel
                sx={{ mt: 2 }}
                control={
                  <Switch
                    checked={!!formData.isPortrait}
                    onChange={(e) => setFormData({ ...formData, isPortrait: e.target.checked })}
                  />
                }
                label={t('videos.portrait')}
              />
            </ModalFormField>

          </ModalGridContainer>

          {formData.categories.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Παρακαλώ επιλέξτε τουλάχιστον μία κατηγορία
            </Alert>
          )}

          <Box sx={{ mt: 3 }}>
            <VideoPdfsManager
              videoId={video?._id}
              pdfs={formData.pdfs}
              onChange={(pdfs) => setFormData({ ...formData, pdfs })}
            />
          </Box>

        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            Ακύρωση
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : 'Αποθήκευση'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default VideoForm;